<script setup>
import { DateInput, DmButton, DmTag, Icon, List, ListBody, ListCell, ListColumn, ListHeader, ListRow, ListTypingFilter } from 'dmui_components'
import getSafeIconName from '@/utility/safeIconName'
import ExpectedReportRepository from '../repository/ExpectedReportRepository.js'
import NotPublishingModal from './NotPublishingModal.vue'
import { useRouter } from 'vue-router'
import { ref } from 'vue'

const DEBOUNCE_TIMER = 500
const props = defineProps({
	reports: {
		type: Array,
		default: undefined
	},
	search: {
		type: Object,
		default: () => ({})
	},
	readonly: {
		type: Boolean,
		default: false
	}
})

const MIN_SEARCH_LIMIT = 2

const emit = defineEmits(['update:search', 'update:monitoring', 'update:reports'])

const router = useRouter()

const selectedReport = ref()
const showNotPublishingModal = ref(false)
const vueDatePickerProps = {
	yearRange: [new Date().getFullYear() - 10, new Date().getFullYear()]
}

function onUpdateSearch(key, value) {
	if (value?.length === 0 || value?.length > MIN_SEARCH_LIMIT) {
		emit('update:search', { ...props.search, [key]: value })
	}
}

function goToReportCreationPage({ document_type, document_year, org_id, expected_date }) {
	router.push(`report/edit?documentType=${document_type}&documentYear=${document_year}&orgId=${org_id}&source=Expected&expectedDate=${expected_date}`)
}

function onUpdateExpectedDate(id, date) {
	ExpectedReportRepository.updateExpectedDate(id, date)
	emit(
		'update:reports',
		props.reports.map((report) => (report.id === id ? { ...report, expected_date: date } : report))
	)
}

function closeNotPublishingModal() {
	selectedReport.value = undefined
	showNotPublishingModal.value = false
}

function openNotPublishingModalForReport(report) {
	selectedReport.value = { ...report }
	showNotPublishingModal.value = true
}

async function updateMonitoringFlags() {
	await ExpectedReportRepository.deleteReportAndUpdateReportMonitoring(selectedReport.value)
	closeNotPublishingModal()
	emit('update:monitoring')
}
</script>
<template>
	<List>
		<ListHeader>
			<ListTypingFilter :timer="DEBOUNCE_TIMER" :data="search.org_name" @update:data="onUpdateSearch('org_name', $event)"> Report </ListTypingFilter>
			<ListColumn> Country & region </ListColumn>
			<ListColumn>Sector</ListColumn>
			<ListColumn>Fiscal month</ListColumn>
			<ListColumn>Market cap</ListColumn>
			<ListColumn>Expected date</ListColumn>
			<ListColumn><!-- Empty header for options column--></ListColumn>
		</ListHeader>
		<ListBody>
			<slot>
				<ListRow v-for="report in reports" :key="report.id">
					<ListCell>
						<span class="multiline-data" :title="report.org_name">
							<span>{{ report.org_name }}</span>
							<div class="data-container">
								<div class="secondary-text bold doc-type">{{ report.document_type }}</div>
								<div>{{ report.document_year }}</div>
							</div>
						</span>
					</ListCell>
					<ListCell>
						<div class="data-container">
							<Icon :name="getSafeIconName('ic_flag', report.country_name)" size="midi" />
							<div class="multiline-data">
								<div class="single-line" :title="report.country_name">{{ report.country_name }}</div>
								<div class="single-line tiny-text" :title="report.region_name">{{ report.region_name }}</div>
							</div>
						</div>
					</ListCell>
					<ListCell>
						<DmTag class="neutral overflowing-tag">
							<Icon :name="getSafeIconName('ic', report.industry_name)" size="mini" />
							<div class="single-line" :title="report.industry_name">{{ report.industry_name }}</div>
						</DmTag>
					</ListCell>
					<ListCell>
						<span>{{ ExpectedReportRepository.fiscalMonths[report.fiscal_month - 1] }}</span>
					</ListCell>
					<ListCell>
						<span>{{ report.market_bucket }}</span>
					</ListCell>
					<ListCell>
						<DateInput
							:id="`${report.id}-expected-date`"
							:data="report.expected_date ? new Date(report.expected_date) : undefined"
							:disabled="readonly"
							placeholder="+ Add date"
							:vue-date-picker-props="vueDatePickerProps"
							@update:data="(newDate) => onUpdateExpectedDate(report.id, newDate)" />
					</ListCell>
					<ListCell>
						<DmButton v-if="!readonly" :id="`${report.id}-add-expected-report`" class="secondary transparent slim" @click="goToReportCreationPage(report)">
							<Icon name="uil-plus" size="midi" />
							<span>Add</span>
						</DmButton>
						<DmButton v-if="!readonly" :id="`${report.id}-dismiss-expected-report`" class="secondary transparent danger slim" @click="openNotPublishingModalForReport(report)">
							<Icon name="uil-times" size="midi" />
							<span>Not publishing</span>
						</DmButton>
					</ListCell>
				</ListRow>
			</slot>
		</ListBody>
	</List>
	<NotPublishingModal
		v-if="showNotPublishingModal"
		:org-name="selectedReport.org_name"
		:document-type="selectedReport.document_type"
		@cancel="closeNotPublishingModal"
		@update:monitoring="updateMonitoringFlags" />
</template>

<style scoped lang="less">
.overflowing-tag {
	max-width: 100%;
	box-sizing: border-box;
}
.data-container {
	display: flex;
	gap: @micro;
	text-transform: capitalize;
	align-items: center;
}
.multiline-data {
	display: flex;
	flex-direction: column;
	.tiny-text {
		color: @gray-80;
		line-height: 14px;
	}
}

:deep(tbody tr) {
	& > td:last-child {
		opacity: 0;
	}
}
:deep(tbody tr):hover {
	background-color: @gray-10;
	& > td:last-child {
		opacity: 1;
	}
}

:deep(td):first-child {
	padding-left: @micro !important;
}
:deep(td):last-child {
	padding-right: @micro !important;
}
:deep(td):not(:last-child) {
	padding-right: @midi !important;
}

:deep(td),
:deep(th) {
	&:not(:last-child) {
		max-width: 100px;
		min-width: 100px;
	}
	&:nth-child(1),
	&:nth-child(2),
	&:nth-child(3) {
		min-width: 190px;
		max-width: 190px;
	}
	&:nth-child(6) {
		min-width: 120px;
		max-width: 120px;
	}

	&:last-child {
		text-align: end;
	}
}

.single-line {
	display: block;
	max-width: 100%;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

:deep(.dp__input) {
	border: 2px solid transparent;
	&:not(:disabled) {
		&:not(:focus):hover {
			border-color: transparent;
			background-color: @gray-10;
		}
	}
	&:focus,
	&:focus:hover {
		border-color: @dm-link;
		box-shadow: none;
	}
}
</style>
